import { useStores } from "api/stores";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { SelectInput } from "components/FormControls/Select/Select";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import {
  selectCurrentStoreData,
  setCurrentStoreData,
} from "reduxState/currentStoreData/currentStoreDataSlice";
import { clearTablePage } from "reduxState/tables/tablesSlice";
import { useStyles } from "./styles";

interface Props {
  tableName: string;
  clearSelection?: () => void;
  clearFilters: () => void;
}

export const Stores = ({ tableName, clearSelection, clearFilters }: Props) => {
  const appDispatch = useAppDispatch();
  const { message } = useMessage();
  const classes = useStyles();
  const { useInfiniteStoreList } = useStores();
  const [searchedQuery, setSearchedQuery] = useState("");
  const originStore = useAppSelector(selectCurrentStoreData);
  const { debouncedValue } = useDebounce(searchedQuery);

  const {
    data: infiniteStoresList,
    size: storesSize,
    setSize: setStoresSize,
    isValidating,
  } = useInfiniteStoreList({
    filters: debouncedValue
      ? [
          {
            condition: "like",
            query: "name",
            value: debouncedValue,
          },
        ]
      : [],
    sortKey: "name",
    order: "asc",
  });

  const { infiniteData: storesList, anyElementsToFetch: anyStoresToFetch } =
    useManageInfiniteScroll(infiniteStoresList || []);

  const items = storesList.map(({ code, name }) => {
    return {
      id: code,
      label: name,
    };
  });

  return (
    <Formik initialValues={{ store: originStore.code }} onSubmit={() => {}}>
      {() => {
        return (
          <Form className={classes.form}>
            <SelectInput
              items={items}
              name="store"
              size="small"
              placeholder={message({ id: "showFromTenant" })}
              className={classes.select}
              onChange={(_e, value) => {
                const storeDetails = storesList.find(
                  (store) => store.code === value,
                );
                appDispatch(clearTablePage({ tableName }));
                clearFilters();

                storeDetails &&
                  appDispatch(
                    setCurrentStoreData({
                      code: storeDetails.code,
                      currency: storeDetails.currency,
                      name: storeDetails.name,
                      id: storeDetails.storeId,
                    }),
                  );

                clearSelection && clearSelection();
              }}
              asyncSearch={(searchedQuery) => setSearchedQuery(searchedQuery)}
              displaySearch
              loadMoreElements={() =>
                !isValidating && setStoresSize(storesSize + 1)
              }
              anyElementsToFetch={anyStoresToFetch}
              initialValue={{
                id: originStore?.code,
                label: originStore?.name,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
